import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable, throwError, timeout} from 'rxjs';
import {ContentModel, DatabaseSchemaModel, FunctionModel, GetLogsDataSourceResponse, GetLogsSummaryResponse, GetSourcesResponse, ProcedureModel, SourceBI, SourceDWH, TableModel, ViewModel} from '../interfaces/source.interface'
import { environment } from '../../../environments/environment';
import { saveAs } from 'file-saver-es';
import { ChartResponse, FilterResponse, FolderResponse, ReportResponse, TabResponse } from '../interfaces/source_db.interface';
import { UserSessionStorageService } from './user-sessions.service';
import { AppConfigService } from '../../app-config.service';
import { FilesResponse } from '../interfaces/source.interface';
import { DirectionModel, GlossaryModel, GlossaryResponseModel } from '../interfaces/glossary.interface';

@Injectable({
  providedIn: 'root',
})

export class PythonService {

  private apiUrl: string;
  constructor(private http: HttpClient, private appConfig: AppConfigService) {
    this.apiUrl = this.appConfig.pythonUrl;
  }


  updateMetaDWH(request: any): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}/update_meta_dwh`, ReportBody = request);
  }

  updateDescriptionDWH(request: any): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}/update_object_description_dwh`, ReportBody = request);
  }

  updateDataCatalogMetaDWH(request: any): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}/om_update_dwh_object`, ReportBody = request);
  }

  updateDataCatalogMetaReport(request: any): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}/om_update_report`, ReportBody = request);
  }

  getObjectLineage(payload: {
    object_type: string;
    object_id: number;
    n_forward: number;
    n_backward: number;
    show_bi: boolean;
    show_unknown: boolean;
  }): Observable<{ dwh_lineages: any[]; bi_lineages: any[] }> {
    const url = `${this.apiUrl}/api/v1/lineage/dwh/get_object_lineage`;
    return this.http.post<{ dwh_lineages: any[]; bi_lineages: any[] }>(url, payload);
  }

  getObjectBILineage(payload: {
    report_id: number;
    n_backward: number;
    show_unknown: boolean;
  }): Observable<{ dwh_lineages: any[]; bi_lineages: any[] }> {
    const url = `${this.apiUrl}/api/v1/lineage/report/get_report_lineage`;
    return this.http.post<{ dwh_lineages: any[]; bi_lineages: any[] }>(url, payload);
  }

  uploadFile(reportId: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    
    const url = `${this.apiUrl}/api/v1/report/pbit/upload/${reportId}`;
    return this.http.post<any>(url, formData, {
      headers: {
        'Accept': 'application/json'
      }
    });
  }

  getReportFilesStatus(reportId: number): Observable<FilesResponse> {
    const url = `${this.apiUrl}/api/v1/report/pbit/get/${reportId}`;
    return this.http.get<FilesResponse>(url, {
      headers: {
        'Accept': 'application/json'
      }
    });
  }


  getReportFiles(reportId: number, fileName: string): Observable<Blob> {
    const url = `${this.apiUrl}/api/v1/report/pbit/get/${reportId}/${encodeURIComponent(fileName)}`;
    return this.http.get(url, {
      responseType: 'blob',
      observe: 'body'
    });
  }


  deleteReportFiles(reportId: number, fileName: string): Observable<string> {
    const url = `${this.apiUrl}/api/v1/report/pbit/delete/${reportId}/${fileName}`;
    return this.http.delete<string>(url, {
      headers: {
        'Accept': 'application/json'
      }
    });
  }


  launchAgent(request: any): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}/api/v1/airflow/run_dag`, ReportBody = request);
  }

  getDagState(request: any): Observable<{ dag_id: string; running: boolean }> {
    return this.http.post<{ dag_id: string; running: boolean }>(`${this.apiUrl}/api/v1/airflow/dag_running_state`, ReportBody = request);
  }

  getDagStats(request: any): Observable<{ dag_id: string; queued: number; running: number; success: number; failed: number }> {
    return this.http.post<{ dag_id: string; queued: number; running: number; success: number; failed: number }>(`${this.apiUrl}/api/v1/airflow/get_dag_stats`, ReportBody = request);
  }

  getLogDatasource(request: any): Observable<GetLogsDataSourceResponse> {
    return this.http.post<GetLogsDataSourceResponse>(`${this.apiUrl}/api/v1/log/details_extract_meta`, ReportBody = request);
  }

  getLogSummary(request: any): Observable<GetLogsSummaryResponse> {
    return this.http.post<GetLogsSummaryResponse>(`${this.apiUrl}/api/v1/log/summary_extract_meta`, ReportBody = request);
  }

  getLogDetailsAnalyze(request: any): Observable<GetLogsDataSourceResponse> {
    return this.http.post<GetLogsDataSourceResponse>(`${this.apiUrl}/api/v1/log/details_analyze`, ReportBody = request);
  }

  getLogSummaryAnalyze(request: any): Observable<GetLogsSummaryResponse> {
    return this.http.post<GetLogsSummaryResponse>(`${this.apiUrl}/api/v1/log/summary_analyze`, ReportBody = request);
  }

  getLogSummaryCrawlImg(request: any): Observable<GetLogsSummaryResponse> {
    return this.http.post<GetLogsSummaryResponse>(`${this.apiUrl}/api/v1/log/summary_crawl_img`, ReportBody = request);
  }

  getLogDetailsCrawlImg(request: any): Observable<GetLogsDataSourceResponse> {
    return this.http.post<GetLogsDataSourceResponse>(`${this.apiUrl}/api/v1/log/details_crawl_img`, ReportBody = request);
  }

  checkConnectionDWH(request: { id: number; dag_id?: string; dag_run_id?: string }): Observable<{ status: string }> {
    const url = `${this.apiUrl}/api/v1/dwh/check_connection`;
    return this.http.post<{ status: string }>(url, request);
  }

  checkConnectionBI(request: { id: number; dag_id?: string; dag_run_id?: string }): Observable<{ status: string }> {
    const url = `${this.apiUrl}/api/v1/report/check_connection`;
    return this.http.post<{ status: string }>(url, request);
  }

  //Возвращает направление компании
  getCompanyDescription(): Observable<DirectionModel> {
    const url = `${this.apiUrl}/api/v1/glossary/direction`;
    return this.http.get<DirectionModel>(url, {
      headers: {
        'Accept': 'application/json'
      }
    });
  }

  //Обновляет направление компании
  setCompanyDescription(direction: string): Observable<DirectionModel> {
    const url = `${this.apiUrl}/api/v1/glossary/direction`;
    return this.http.put<DirectionModel>(url, { direction: direction });
  }

  //Возвращает все строки бизнес-глоссария
  getGlossaryTerms(): Observable<GlossaryResponseModel> {
    const url = `${this.apiUrl}/api/v1/glossary/terms`;
    return this.http.get<GlossaryResponseModel>(url, {
      headers: {
        'Accept': 'application/json'
      }
    });
  }

  //Обновляет существующую строку в бизнес-глоссарии
  setGlossaryTerms(glossary: any): Observable<GlossaryModel> {
    const url = `${this.apiUrl}/api/v1/glossary/terms`;
    const rowId = glossary?.rowId ?? glossary?.row_id;
    const payload = {
      row_id: Number(rowId),
      term: glossary?.term ?? '',
      meaning: glossary?.meaning ?? '',
    };
    return this.http.put<GlossaryModel>(url, payload);
  }

  // Добавляет новую строку в бизнес-глоссарий
  addGlossaryTerm(term: string, meaning: string): Observable<GlossaryModel> {
    const url = `${this.apiUrl}/api/v1/glossary/terms`;
    return this.http.post<GlossaryModel>(url, { term: term ?? '', meaning: meaning ?? '' });
  }

  // Удаляет строку из бизнес-глоссария
  deleteGlossaryTerm(rowId: number): Observable<void> {
    const url = `${this.apiUrl}/api/v1/glossary/terms`;
    return this.http.delete<void>(url, { body: { row_id: rowId } });
  }

  // Экспорт глоссария в Excel
  exportGlossaryExcel(language: string): Observable<Blob> {
    const url = `${this.apiUrl}/api/v1/glossary/export`;
    return this.http.get(url, {
      params: { format: 'excel', language: language },
      responseType: 'blob',
    });
  }

  // Импорт глоссария из Excel
  importGlossaryExcel(file: File): Observable<any> {
    const url = `${this.apiUrl}/api/v1/glossary/import`;
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(url, formData, {
      params: { format: 'excel' },
      headers: { Accept: 'application/json' },
    });
  }
}
